// Structured-data (JSON-LD) builders for rich results.

export const PROFILE = {
  name: "Amiran Azaladze",
  altName: "ამირან აზალაძე",
  jobTitle: "Entrepreneur & Business Leader",
  image: "/about/amiran-speaking.jpg",
  description:
    "Amiran Azaladze is an entrepreneur and business leader — founder of B2B Hub, Astra Terminal, FX Astra and Dezinfector, and author of 'Forex — 6 Steps to Financial Independence'. He builds SaaS, fintech and service ventures worldwide.",
  sameAs: [
    "https://www.instagram.com/amiran_azaladze/",
    "https://www.linkedin.com/in/amirani/",
    "https://x.com/AmiranAzaladze",
    "https://www.crunchbase.com/person/amiran-azaladze",
  ],
  knowsAbout: [
    "Entrepreneurship",
    "Venture Building",
    "Fintech",
    "SaaS",
    "Forex Trading",
    "Fundraising",
    "Business Strategy",
    "Leadership",
  ],
};

export const COMPANIES = [
  {
    id: "b2bhub",
    name: "B2B Hub",
    url: "https://b2bhub.ltd",
    description:
      "Business operating system — AI website & app builders, company formation, CRM, HR, payroll, invoicing and a developer API.",
  },
  {
    id: "astraterminal",
    name: "Astra Terminal",
    url: "https://astraterminal.net",
    description:
      "White-label brokerage technology — trading terminal, admin dashboard, client portal and on-chain crypto payments.",
  },
  {
    id: "fxastra",
    name: "FX Astra",
    url: "https://fxastra.com",
    description:
      "Forex broker intelligence platform — broker comparison, live charts, economic calendar and market news.",
  },
  {
    id: "dezinfector",
    name: "Dezinfector",
    url: "https://dezinfector.com.ua",
    description: "Professional 24/7 disinfection and pest-control services.",
  },
];

export const BOOK = {
  name: "Forex — 6 Steps to Financial Independence",
  altName: "ფორექსი",
  url: "https://www.lit.ge/book/156425-foreqsi-amiran-azaladze/",
  cover: "/book/forex-cover.jpg",
  inLanguage: ["ka"],
};

const abs = (origin, path) => (path?.startsWith("http") ? path : `${origin}${path}`);

export function buildSiteGraph(origin) {
  const personId = `${origin}/#person`;
  const websiteId = `${origin}/#website`;

  const person = {
    "@type": "Person",
    "@id": personId,
    name: PROFILE.name,
    alternateName: PROFILE.altName,
    url: `${origin}/`,
    image: abs(origin, PROFILE.image),
    jobTitle: PROFILE.jobTitle,
    hasOccupation: {
      "@type": "Occupation",
      name: "Entrepreneur",
      occupationalCategory: "Founder & Business Leader",
    },
    gender: "Male",
    description: PROFILE.description,
    sameAs: PROFILE.sameAs,
    knowsAbout: PROFILE.knowsAbout,
    worksFor: COMPANIES.map((c) => ({ "@id": `${origin}/#${c.id}` })),
  };

  const orgs = COMPANIES.map((c) => ({
    "@type": "Organization",
    "@id": `${origin}/#${c.id}`,
    name: c.name,
    url: c.url,
    description: c.description,
    founder: { "@id": personId },
    sameAs: [c.url],
  }));

  const book = {
    "@type": "Book",
    "@id": `${origin}/#book-forex`,
    name: BOOK.name,
    alternateName: BOOK.altName,
    url: BOOK.url,
    author: { "@id": personId },
    inLanguage: BOOK.inLanguage,
    image: abs(origin, BOOK.cover),
    about: "Forex trading and personal finance",
    bookFormat: "https://schema.org/EBook",
  };

  const website = {
    "@type": "WebSite",
    "@id": websiteId,
    url: `${origin}/`,
    name: PROFILE.name,
    inLanguage: "en",
    publisher: { "@id": personId },
  };

  const profilePage = {
    "@type": "ProfilePage",
    "@id": `${origin}/#webpage`,
    url: `${origin}/`,
    name: `${PROFILE.name} — ${PROFILE.jobTitle}`,
    isPartOf: { "@id": websiteId },
    about: { "@id": personId },
    primaryImageOfPage: abs(origin, PROFILE.image),
  };

  return {
    "@context": "https://schema.org",
    "@graph": [website, profilePage, person, ...orgs, book],
  };
}

const stripHtml = (html = "") =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

export function buildArticleGraph(post, origin) {
  const url = `${origin}/blog/${post.slug}`;
  const img = abs(origin, post.cover_image || "/og-image.jpg");
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `${url}#article`,
        mainEntityOfPage: url,
        url,
        headline: post.title,
        description: post.excerpt,
        articleBody: stripHtml(post.body),
        image: [img],
        datePublished: post.created_at,
        dateModified: post.updated_at || post.created_at,
        inLanguage: "en",
        keywords: post.tag,
        author: { "@type": "Person", "@id": `${origin}/#person`, name: PROFILE.name },
        publisher: {
          "@type": "Organization",
          name: PROFILE.name,
          logo: { "@type": "ImageObject", url: abs(origin, "/og-image.jpg") },
        },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${origin}/#blog` },
          { "@type": "ListItem", position: 3, name: post.title, item: url },
        ],
      },
    ],
  };
}

export const getOrigin = () =>
  typeof window !== "undefined" && window.location ? window.location.origin : "";
